import React, { useState } from 'react';
import { Table, Container, Badge, Form, Row, Col, Button, Card, InputGroup, Modal } from 'react-bootstrap';
import { FaSearch, FaFilter, FaEnvelope, FaPhone, FaCalendarAlt, FaHandshake, FaCheckCircle, FaClock, FaSpinner, FaThumbsUp } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '../../components/layout/Loader';
import Message from '../../components/layout/Message';
import Paginate from '../../components/ui/Paginate';
import Meta from '../../components/layout/Meta';
import { useGetPartnersQuery, useUpdatePartnerStatusMutation } from '../../slices/partnerApiSlice';

const PartnerListScreen = () => {
    const { pageNumber: pageParam } = useParams();
    const [keyword, setKeyword] = useState('');
    const [status, setStatus] = useState('');
    const [pageNumber, setPageNumber] = useState(pageParam || 1);
    const pageSize = 20;

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState(null);

    const { data, isLoading, error, refetch } = useGetPartnersQuery({
        pageNumber,
        pageSize,
        keyword,
        status
    });

    const [updatePartnerStatus, { isLoading: isUpdating }] = useUpdatePartnerStatusMutation();

    const partners = data?.partners || data?.data || data?.results || data?.requests || [];
    const page = data?.page || 1;
    const pages = data?.pages || 1;
    const total = data?.total || partners.length || 0;

    const handleSearch = (e) => {
        e.preventDefault();
        setPageNumber(1);
        refetch();
    };

    const handleStatusUpdate = async (newStatus) => {
        if (!selectedPartner) return;
        try {
            await updatePartnerStatus({ id: selectedPartner._id, status: newStatus }).unwrap();
            toast.success(`Estado actualizado a ${newStatus}`);
            refetch();
            setShowModal(false);
        } catch (err) {
            toast.error(err?.data?.message || err.error || 'Error al actualizar estado');
        }
    };

    const openDetailsModal = (partner) => {
        setSelectedPartner(partner);
        setShowModal(true);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Pending':
                return <Badge bg="warning" text="dark" className="px-3 py-2 rounded-pill"><FaClock className="me-1"/> Pendiente</Badge>;
            case 'Contacted':
                return <Badge bg="info" className="px-3 py-2 rounded-pill"><FaPhone className="me-1"/> Atendido</Badge>;
            case 'In Process':
                return <Badge bg="primary" className="px-3 py-2 rounded-pill"><FaSpinner className="me-1"/> En Proceso</Badge>;
            case 'Converted':
                return <Badge bg="success" className="px-3 py-2 rounded-pill"><FaThumbsUp className="me-1"/> Aceptado</Badge>;
            default:
                return <Badge bg="secondary" className="px-3 py-2 rounded-pill">{status}</Badge>;
        }
    };

    const getNextStatusOptions = (currentStatus) => {
        const options = [];
        if (currentStatus === 'Pending') options.push({ value: 'Contacted', label: 'Marcar como Atendido', variant: 'info' });
        if (currentStatus === 'Contacted') options.push({ value: 'In Process', label: 'Marcar En Proceso', variant: 'primary' });
        if (currentStatus === 'In Process') options.push({ value: 'Converted', label: 'Aceptar Partner', variant: 'success' });
        return options;
    };

    return (
        <Container fluid className="px-md-5 py-4">
            <Meta title="Solicitudes de Partners" />
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1 d-flex align-items-center gap-2">
                        <FaHandshake className="text-primary" /> Solicitudes de Partners
                    </h2>
                    <p className="text-muted mb-0">
                        Gestiona las empresas interesadas en integración Siigo.
                        {total > 0 && <span className="ms-2 badge bg-light text-dark border">{total} registros</span>}
                    </p>
                </div>
            </div>

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-4">
                    <Form onSubmit={handleSearch}>
                        <Row className="g-3">
                            <Col md={6} lg={5}>
                                <Form.Group controlId="keyword">
                                    <Form.Label className="small text-muted fw-bold" column='sm'>
                                        BÚSQUEDA
                                    </Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text className="bg-light border-end-0">
                                            <FaSearch className="text-muted" />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            placeholder="Buscar por empresa, email o teléfono..."
                                            value={keyword}
                                            onChange={(e) => setKeyword(e.target.value)}
                                            className="border-start-0 bg-light"
                                        />
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col md={4} lg={3}>
                                <Form.Group controlId="status">
                                    <Form.Label className="small text-muted fw-bold" column='sm'>
                                        ESTADO
                                    </Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text className="bg-light border-end-0">
                                            <FaFilter className="text-muted" />
                                        </InputGroup.Text>
                                        <Form.Select
                                            value={status}
                                            onChange={(e) => {
                                                setStatus(e.target.value);
                                                setPageNumber(1);
                                            }}
                                            className="border-start-0 bg-light"
                                        >
                                            <option value="">Todos los estados</option>
                                            <option value="Pending">Pendiente</option>
                                            <option value="Contacted">Atendido</option>
                                            <option value="In Process">En Proceso</option>
                                            <option value="Converted">Aceptado</option>
                                        </Form.Select>
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col md={2} className="d-flex align-items-end">
                                <Button type="submit" variant="primary" className="w-100 fw-bold">
                                    Buscar
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {isLoading ? (
                <Loader />
            ) : error ? (
                <Message variant="danger">{error?.data?.message || error.error}</Message>
            ) : (
                <>
                    {partners.length === 0 ? (
                        <div className="text-center py-5 bg-light rounded-3">
                            <FaHandshake size={50} className="text-muted mb-3" opacity={0.5} />
                            <h4 className="text-muted">No se encontraron solicitudes</h4>
                            <p className="text-muted small">Intenta ajustar los filtros de búsqueda</p>
                        </div>
                    ) : (
                        <>
                            {/* Desktop View */}
                            <div className="d-none d-md-block bg-white rounded-3 shadow-sm overflow-hidden">
                                <Table hover responsive className="mb-0 align-middle text-nowrap">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="py-3 ps-4 text-muted small fw-bold border-0" style={{ width: '25%' }}>EMPRESA</th>
                                            <th className="py-3 text-center text-muted small fw-bold border-0" style={{ width: '25%' }}>CONTACTO</th>
                                            <th className="py-3 text-center text-muted small fw-bold border-0" style={{ width: '15%' }}>FECHA</th>
                                            <th className="py-3 text-center text-muted small fw-bold border-0" style={{ width: '15%' }}>ESTADO</th>
                                            <th className="py-3 pe-4 text-center text-muted small fw-bold border-0" style={{ width: '20%' }}>ACCIONES</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {partners.map((partner) => (
                                            <tr key={partner._id}>
                                                <td className="ps-4 py-3">
                                                    <div className="fw-bold text-dark text-truncate" style={{ maxWidth: '250px' }} title={partner.companyName}>
                                                        {partner.companyName}
                                                    </div>
                                                    <div className="small text-muted">ID: {partner._id.substring(partner._id.length - 6)}</div>
                                                </td>
                                                <td className="py-3 text-center">
                                                    <div className="d-flex flex-column align-items-center gap-1">
                                                        <div className="d-flex align-items-center gap-2 text-secondary">
                                                            <FaEnvelope size={14} className="text-muted" />
                                                            <a href={`mailto:${partner.email}`} className="text-decoration-none text-secondary small text-truncate" style={{ maxWidth: '200px' }} title={partner.email}>
                                                                {partner.email}
                                                            </a>
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2 text-secondary">
                                                            <FaPhone size={14} className="text-muted" />
                                                            <span className="small">{partner.phone}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-3 text-center text-secondary">
                                                    <div className="d-flex flex-column align-items-center">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <FaCalendarAlt size={12} />
                                                            <span>{new Date(partner.createdAt).toLocaleDateString()}</span>
                                                        </div>
                                                        <div className="small text-muted">
                                                            {new Date(partner.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="py-3 text-center">
                                                    {getStatusBadge(partner.status)}
                                                </td>
                                                <td className="pe-4 py-3 text-center">
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        className="rounded-pill px-3"
                                                        onClick={() => openDetailsModal(partner)}
                                                    >
                                                        Gestionar
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Mobile View */}
                            <div className="d-md-none d-flex flex-column gap-3">
                                {partners.map((partner) => (
                                    <Card key={partner._id} className="border-0 shadow-sm">
                                        <Card.Body className="p-3">
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div>
                                                    <h6 className="fw-bold mb-1 text-primary">{partner.companyName}</h6>
                                                    <small className="text-muted">ID: ...{partner._id.substring(partner._id.length - 6)}</small>
                                                </div>
                                                {getStatusBadge(partner.status)}
                                            </div>

                                            <div className="d-flex flex-column gap-2 mb-3">
                                                <div className="d-flex align-items-center gap-2 text-secondary small">
                                                    <FaEnvelope className="text-muted" />
                                                    <a href={`mailto:${partner.email}`} className="text-decoration-none text-secondary">
                                                        {partner.email}
                                                    </a>
                                                </div>
                                                <div className="d-flex align-items-center gap-2 text-secondary small">
                                                    <FaPhone className="text-muted" />
                                                    {partner.phone}
                                                </div>
                                                <div className="d-flex align-items-center gap-2 text-secondary small">
                                                    <FaCalendarAlt className="text-muted" />
                                                    {new Date(partner.createdAt).toLocaleDateString()}
                                                </div>
                                            </div>

                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="w-100 rounded-pill"
                                                onClick={() => openDetailsModal(partner)}
                                            >
                                                Gestionar Solicitud
                                            </Button>
                                        </Card.Body>
                                    </Card>
                                ))}
                            </div>

                            <div className="mt-4 d-flex justify-content-center">
                                <Paginate
                                    pages={pages}
                                    page={page}
                                    isAdmin={true}
                                    keyword={keyword ? keyword : ''}
                                />
                            </div>
                        </>
                    )}
                </>
            )}

            {/* Modal de Gestión */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold text-primary">Gestión de Solicitud</Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    {selectedPartner && (
                        <>
                            <div className="bg-light p-3 rounded mb-4">
                                <h5 className="fw-bold mb-3">{selectedPartner.companyName}</h5>
                                <div className="d-flex flex-column gap-2 text-secondary">
                                    <div className="d-flex align-items-center gap-2">
                                        <FaEnvelope className="text-muted" /> {selectedPartner.email}
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <FaPhone className="text-muted" /> {selectedPartner.phone}
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <FaClock className="text-muted" /> Estado actual: <strong>{getStatusBadge(selectedPartner.status)}</strong>
                                    </div>
                                </div>
                            </div>

                            <h6 className="fw-bold mb-3 text-muted small">ACCIONES DISPONIBLES</h6>
                            <div className="d-grid gap-2">
                                {getNextStatusOptions(selectedPartner.status).map((option) => (
                                    <Button
                                        key={option.value}
                                        variant={option.variant}
                                        className="py-2 fw-bold d-flex align-items-center justify-content-center gap-2"
                                        onClick={() => handleStatusUpdate(option.value)}
                                        disabled={isUpdating}
                                    >
                                        {isUpdating ? <FaSpinner className="fa-spin" /> : <FaCheckCircle />}
                                        {option.label}
                                    </Button>
                                ))}
                                {getNextStatusOptions(selectedPartner.status).length === 0 && (
                                    <div className="text-center text-muted py-2">
                                        <FaCheckCircle className="text-success mb-2" size={24} />
                                        <p className="mb-0">Esta solicitud ya ha sido completada.</p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default PartnerListScreen;
